import {
  STORY_VIDEO_CONTENT_TYPES,
  type PublicStoryMedia,
  type StoryVideoContentType,
} from '@fois-utsavvoice/shared';
import { listPublicStoryMedia } from '../api/stories.js';

const BUSY_STATUSES = ['UPLOADING', 'UPLOADED', 'VALIDATING', 'PROCESSING'] as const;
const PROCESSING_STATUSES = ['UPLOADED', 'VALIDATING', 'PROCESSING'] as const;
const POLL_MS = 400;
const POLL_TRIES = 45;

export function isStoryMediaBusy(status: string): boolean {
  return (BUSY_STATUSES as readonly string[]).includes(status);
}

export function isStoryMediaProcessing(status: string): boolean {
  return (PROCESSING_STATUSES as readonly string[]).includes(status);
}

export function storyMediaStatusLabel(status: string): string {
  switch (status) {
    case 'READY':
    case 'APPROVED':
      return 'Ready';
    case 'UPLOADING':
      return 'Uploading';
    case 'UPLOADED':
    case 'VALIDATING':
    case 'PROCESSING':
      return 'Processing';
    case 'FAILED_RETRYABLE':
      return 'Needs retry';
    case 'REJECTED_AUTOMATED':
      return 'Not accepted';
    default:
      return status;
  }
}

export function mergeLocalMediaItems<T extends { localId: string; mediaId?: string }>(
  current: T[],
  listed: T[],
): T[] {
  const listedIds = new Set(
    listed.map((item) => item.mediaId).filter((id): id is string => Boolean(id)),
  );
  const extras = current.filter((item) => !item.mediaId || !listedIds.has(item.mediaId));
  return [...listed, ...extras];
}

export async function waitForStoryMediaToSettle(
  token: string,
  mediaId: string,
  current: PublicStoryMedia,
): Promise<PublicStoryMedia> {
  if (!isStoryMediaProcessing(current.status)) {
    return current;
  }
  let latest = current;
  for (let attempt = 0; attempt < POLL_TRIES; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    const listed = await listPublicStoryMedia(token);
    const found = listed.items.find((item) => item.mediaId === mediaId);
    if (!found) {
      continue;
    }
    latest = found;
    if (!isStoryMediaProcessing(found.status)) {
      return found;
    }
  }
  return {
    ...latest,
    status: 'FAILED_RETRYABLE',
  };
}

export function storyVideoMimeForFile(file: File): StoryVideoContentType | undefined {
  if ((STORY_VIDEO_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return file.type as StoryVideoContentType;
  }
  if (file.type === 'video/x-m4v' || file.type === 'video/mpeg') {
    return 'video/mp4';
  }
  const name = file.name.toLowerCase();
  if (name.endsWith('.mp4') || name.endsWith('.m4v')) {
    return 'video/mp4';
  }
  if (name.endsWith('.mov') || name.endsWith('.qt')) {
    return 'video/quicktime';
  }
  if (name.endsWith('.webm')) {
    return 'video/webm';
  }
  if (file.type.startsWith('video/')) {
    return 'video/mp4';
  }
  return undefined;
}
